import { PropsWithChildren } from 'react'
import { Link, generatePath, useLocation } from 'react-router-dom'

import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useViewType } from '@/modules/shared/hooks/useViewType'

interface PPNewDetailLinkProps extends PropsWithChildren {
  id?: string | number
  secondaryId?: string | number // for use on links that require two ids
  type?:
    | 'purchase-order'
    | 'requisition'
    | 'invoice'
    | 'credit-note'
    | 'catalog'
    | 'catalog-product'
    | 'supplier'
    | 'supplier-directory'
    | 'supplier-directory-products'
    | 'user'
    | 'buy-list'
    | 'stocktake'
    | 'stock-location'
    | 'stock-item'
    | 'transfer'
    | 'receiving-document'
    | 'approval-workflow-executions'
    | 'pos-batch'
    | 'pos-item'
    | 'pos-outlet'
    | 'public-shop'
    | 'my-purchaser'
    | 'export'
    | 'recipe'
    | 'recipe-card'
    | 'menu'
    | 'product'
    | 'tag'
    | 'allergen'
    | 'paperless-email'
    | 'budget'
    | 'account-code'
    | 'organisation'
    | 'paperless-email-action-item'
    | 'utility-organisation'
    | 'utility-ap-system'
    | 'utility-trade-relationship'
    | 'utility-hierarchy'
    | 'utility-ap-export-format'
    | 'utility-groom-task'
    | 'utility-billing-group'
    | 'utility-process-document'
    | 'utility-public-shop-order'
    | 'utility-report'
    | 'utility-destination'
    | 'utility-paperless-email'
    | 'utility-account-code'
    | 'utility-organisation-account'
    | 'utility-budget'
    | 'utility-paperless-email-action-item'
    | 'utility-allergen'
  openNewTab?: boolean
  className?: string
  keepSearch?: boolean
  testId?: string
}

export function PPNewDetailLink({
  id,
  secondaryId,
  type,
  openNewTab = false,
  children,
  className,
  keepSearch = false,
  testId,
}: PPNewDetailLinkProps) {
  const { currentHolder } = useSessionUser()
  const openNewTabProps = openNewTab ? { target: '_blank', rel: 'noreferrer' } : {}
  let linkTo = ''
  const defaultClassName = 'text-primary'
  const { isUtility } = useViewType()
  const { search } = useLocation()

  // Safety check: if required ids are missing, render children without link
  const requiresSecondaryId = ['catalog-product', 'receiving-document'].includes(type!)
  if (!id || (requiresSecondaryId && !secondaryId)) {
    return <>{children}</>
  }

  switch (type) {
    case 'purchase-order':
      linkTo = '/purchase-orders/:id/summary'
      break
    case 'requisition':
      linkTo = '/requisitions/:id/summary'
      break
    case 'invoice':
      linkTo = '/invoices/:id/summary'
      break
    case 'credit-note':
      linkTo = '/credit-notes/:id/summary'
      break
    case 'catalog':
      linkTo = '/catalogs/:id/products'
      break
    case 'catalog-product':
      linkTo = '/catalogs/:id/products/:secondaryId/product-details'
      break
    case 'supplier':
      linkTo = '/my-suppliers/:id/supplier-details'
      break
    case 'supplier-directory':
      linkTo = '/supplier-directory/:id/supplier-details'
      break
    case 'supplier-directory-products':
      linkTo = '/supplier-directory/:id/products'
      break
    case 'user':
      linkTo = '/users/:id/general'
      break
    case 'buy-list':
      linkTo = '/buy-lists/:id/products'
      break
    case 'stocktake':
      linkTo = '/inventory/stocktakes/:id/stock-count'
      break
    case 'stock-item':
      linkTo = '/inventory/stock-items/:id/stock-locations'
      break
    case 'stock-location':
      linkTo = '/inventory/stock-locations/:id/stock-items'
      break
    case 'receiving-document':
      linkTo = '/purchase-orders/:secondaryId/receiving-notes/:id/legacy/summary'

      if (currentHolder) {
        linkTo = '/purchase-orders/:secondaryId/receiving-notes/:id/summary'
      }
      break
    case 'transfer':
      linkTo = '/inventory/transfers/:id/transfer-items'
      break
    case 'pos-batch':
      linkTo = '/point-of-sale/batch-sales/:id/sales'
      break
    case 'pos-item':
      linkTo = '/point-of-sale/items/:id/details'
      break
    case 'pos-outlet':
      linkTo = '/point-of-sale/outlets/:id/settings'
      break
    case 'approval-workflow-executions':
      linkTo = '/settings/approval-workflows/:id/executions'
      break
    case 'public-shop':
      linkTo = '/shop/:id'
      break
    case 'my-purchaser':
      linkTo = '/my-purchasers/:id/purchaser-details'
      break
    case 'export':
      linkTo = '/invoices/export-center/:id/view'
      break
    case 'recipe':
      linkTo = '/recipes/:id/ingredients'
      break
    case 'recipe-card':
      linkTo = '/recipes/:id/card'
      break
    case 'menu':
      linkTo = '/menus/:id/recipes'
      break
    case 'product':
      linkTo = '/products/:id/details'
      break
    case 'tag':
      // Tags use the base details route for Settings to keep URLs stable (no `/settings` suffix).
      linkTo = '/products/tags/:id/products'
      break
    case 'allergen':
      linkTo = '/recipes/allergens/:id/settings'
      break
    case 'paperless-email':
      linkTo = '/paperless-emails/:id/paperless-email'
      break
    case 'budget':
      linkTo = '/budgets/:id/usage'
      break
    case 'account-code':
      linkTo = '/settings/ap-system/accounts/:id/general'
      break
    case 'paperless-email-action-item':
      linkTo = '/paperless-emails/action-items/:id/action-item'
      break
    case 'utility-organisation':
      linkTo = '/utilities/organisations/:id/general'
      break
    case 'organisation':
      linkTo = '/organisations/:id/general'
      break
    case 'utility-ap-system':
      linkTo = '/utilities/ap-systems/:id/general'
      break
    case 'utility-trade-relationship':
      linkTo = '/utilities/organisations/trade-relationships/:id/general'
      break
    case 'utility-hierarchy':
      linkTo = '/utilities/organisations/hierarchies/:id'
      break
    case 'utility-ap-export-format':
      linkTo = '/utilities/ap-export-formats/:id/general'
      break
    case 'utility-groom-task':
      linkTo = '/utilities/products/grooming-tasks/:id'
      break
    case 'utility-billing-group':
      linkTo = '/utilities/organisations/billing-groups/:id/members'
      break
    case 'utility-process-document':
      linkTo = '/utilities/ai-scanning/process-documents/:id/process-document'
      break
    case 'utility-public-shop-order':
      linkTo = '/utilities/public-shop/orders/:id/summary'
      break
    case 'utility-report':
      linkTo = '/utilities/reports/:id/general'
      break
    case 'utility-destination':
      linkTo = '/utilities/organisations/destinations/:id/settings'
      break
    case 'utility-paperless-email':
      linkTo = '/utilities/ai-scanning/paperless-emails/:id/paperless-email'
      break
    case 'utility-account-code':
      linkTo = '/utilities/ap-systems/account-codes/:id/general'
      break
    case 'utility-organisation-account':
      linkTo = '/utilities/ap-systems/organisation-accounts/:id/general'
      break
    case 'utility-budget':
      linkTo = '/utilities/ap-systems/budgets/:id/usage'
      break
    case 'utility-paperless-email-action-item':
      linkTo = '/utilities/ai-scanning/paperless-emails/action-items/:id/action-item'
      break
    case 'utility-allergen':
      linkTo = '/utilities/products/allergens/:id/settings'
      break
    default:
      return <>{children}</>
  }

  return (
    <Link
      to={generatePath(
        `${isUtility && !type.includes('utility') ? '/utilities' : ''}${linkTo}${keepSearch ? `${search}` : ''}`,
        {
          id,
          secondaryId,
        }
      )}
      className={className || defaultClassName}
      {...openNewTabProps}
      data-testid={testId}
    >
      {children}
    </Link>
  )
}
